"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Calendar,
  ChevronDown,
  DollarSign,
  Eye,
  LayoutGrid,
  MousePointerClick,
  Target,
} from "lucide-react";
import { useLocale } from "@/lib/useLocale";
import { useDictionary } from "@/lib/useDictionary";
import { TypeAnimation } from "react-type-animation";

const PLATFORMS = [
  { name: "Meta", src: "/icon/fb.png" },
  { name: "GA4", src: "/icon/ga4.png" },
  { name: "Google Ads", src: "/icon/gads.png" },
  { name: "Threads", src: "/icon/threads.png" },
  { name: "LINE", src: "/icon/line.png" },
];

const TABS = [
  { key: "all", src: null, label: null },
  { key: "meta", src: "/icon/fb.png", label: "Meta" },
  { key: "gads", src: "/icon/gads.png", label: "Google Ads" },
  { key: "line", src: "/icon/line.png", label: "LINE" },
  { key: "threads", src: "/icon/threads.png", label: "Threads" },
  { key: "ga4", src: "/icon/ga4.png", label: "GA4" },
];
const ACTIVE_TAB = "all";

const SPARKS: Record<string, number[]> = {
  impr: [8, 12, 9, 15, 13, 18, 16, 22, 20, 26],
  click: [10, 8, 14, 11, 17, 15, 20, 18, 24, 22],
  conv: [6, 10, 8, 13, 12, 16, 14, 20, 19, 24],
  value: [9, 13, 11, 16, 15, 21, 19, 24, 23, 28],
};

const TABLE_ROWS = [
  {
    name: "Meta",
    icon: "/icon/fb.png",
    impr: "10,824,654",
    imprBar: 42,
    click: "256,845",
    clickBar: 42,
    conv: "4,682",
    cvr: "1.82%",
    value: "$3,256,800",
  },
  {
    name: "Google Ads",
    icon: "/icon/gads.png",
    impr: "7,598,214",
    imprBar: 30,
    click: "171,203",
    clickBar: 28,
    conv: "3,102",
    cvr: "1.81%",
    value: "$2,248,600",
  },
  {
    name: "LINE",
    icon: "/icon/line.png",
    impr: "4,502,132",
    imprBar: 18,
    click: "91,342",
    clickBar: 15,
    conv: "1,901",
    cvr: "2.08%",
    value: "$1,342,100",
  },
  {
    name: "Instagram",
    icon: "/icon/ig.png",
    impr: "2,945,732",
    imprBar: 12,
    click: "66,132",
    clickBar: 11,
    conv: "1,123",
    cvr: "1.70%",
    value: "$876,300",
  },
  {
    name: "Threads",
    icon: "/icon/threads.png",
    impr: "1,736,214",
    imprBar: 7,
    click: "34,821",
    clickBar: 6,
    conv: "745",
    cvr: "2.14%",
    value: "$518,500",
  },
  {
    name: null,
    icon: null,
    impr: "847,316",
    imprBar: 4,
    click: "22,902",
    clickBar: 4,
    conv: "492",
    cvr: "2.15%",
    value: "$500,000",
  },
];

const SHARE = [
  { name: "Meta", pct: "38.1%", color: "#1877F2" },
  { name: "Google Ads", pct: "26.7%", color: "#F9A825" },
  { name: "LINE", pct: "15.8%", color: "#06C755" },
  { name: "Instagram", pct: "10.3%", color: "#E1306C" },
  { name: "Threads", pct: "6.1%", color: "#111827" },
  { name: null, pct: "3.0%", color: "#CBD5E1" },
];

const DONUT_GRADIENT =
  "conic-gradient(#1877F2 0 38.1%, #F9A825 38.1% 64.8%, #06C755 64.8% 80.6%, #E1306C 80.6% 90.9%, #111827 90.9% 97%, #CBD5E1 97% 100%)";

/* 折線圖：用 sin 疊波產生，純函式所以 server / client 結果一致 */
const TREND_LEN = 31;
const CHART_W = 600;
const CHART_H = 190;
const HL = 20; // 被 tooltip 標住的那天（08/21）

const trendImpr = Array.from(
  { length: TREND_LEN },
  (_, i) =>
    700 + Math.sin(i * 0.62) * 150 + Math.sin(i * 0.27 + 1.1) * 110 + i * 3,
);
const trendClick = Array.from(
  { length: TREND_LEN },
  (_, i) =>
    330 + Math.sin(i * 0.55 + 0.8) * 90 + Math.sin(i * 0.31) * 70 + i * 1.5,
);

const V_MAX = Math.max(...trendImpr, ...trendClick) * 1.12;
const xAt = (i: number) => (i / (TREND_LEN - 1)) * CHART_W;
const yAt = (v: number) => CHART_H - (v / V_MAX) * CHART_H;
const toPoints = (arr: number[]) =>
  arr.map((v, i) => `${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).join(" ");

const IMPR_POINTS = toPoints(trendImpr);
const CLICK_POINTS = toPoints(trendClick);
const X_LABELS = [
  "08/01",
  "08/06",
  "08/11",
  "08/16",
  "08/21",
  "08/26",
  "08/31",
];
const Y_LABELS = ["1.2M", "900K", "600K", "300K", "0"];

function Sparkline({ data, stroke }: { data: number[]; stroke: string }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const span = max - min || 1;
  const width = (data.length - 1) * 10;
  const points = data
    .map((v, i) => `${i * 10},${(28 - ((v - min) / span) * 24).toFixed(1)}`)
    .join(" ");
  return (
    <svg
      viewBox={`0 0 ${width} 30`}
      preserveAspectRatio="none"
      className="h-5 w-full"
      aria-hidden="true"
    >
      <polyline
        points={points}
        fill="none"
        stroke={stroke}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

export default function Hero() {
  const locale = useLocale();
  const dict = useDictionary();

  const kpis = [
    {
      key: "impr",
      label: dict.hero.kpiImpressions,
      value: "28.45M",
      delta: "12.6%",
      Icon: Eye,
      tint: "bg-blue-50 text-blue-500",
      stroke: "#3b82f6",
    },
    {
      key: "click",
      label: dict.hero.kpiClicks,
      value: "643,245",
      delta: "8.3%",
      Icon: MousePointerClick,
      tint: "bg-violet-50 text-violet-500",
      stroke: "#8b5cf6",
    },
    {
      key: "conv",
      label: dict.hero.kpiConversions,
      value: "12,045",
      delta: "15.7%",
      Icon: Target,
      tint: "bg-sky-50 text-sky-500",
      stroke: "#0ea5e9",
    },
    {
      key: "value",
      label: dict.hero.kpiConvValue,
      value: "$8,742,300",
      delta: "21.4%",
      Icon: DollarSign,
      tint: "bg-amber-50 text-amber-500",
      stroke: "#f59e0b",
    },
  ];

  return (
    <section className="relative flex min-h-screen items-center overflow-hidden gradient-blue">
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden"
        >
          <div className="hero-grid absolute inset-0 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,#000_35%,transparent_100%)]" />
          <div className="absolute left-1/2 top-0 h-px w-2/3 -translate-x-1/2 bg-gradient-to-r from-transparent via-[var(--gradient-start)] to-transparent" />
          <div className="absolute left-[calc(50%+96px)] top-[96px] hidden h-12 w-12 bg-[var(--gradient-start)]/10 lg:block" />
          <div className="absolute left-[calc(50%-288px)] top-[48px] hidden h-12 w-12 bg-[var(--blue-300)]/20 lg:block" />
        </div>
      </div>

      <div className="relative mx-auto w-full min-w-0 max-w-[1400px] px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="grid items-center gap-14 lg:grid-cols-[minmax(0,0.88fr)_minmax(0,1.12fr)] lg:gap-10">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="min-w-0 text-center lg:text-left"
          >
            <h1 className="relative">
              <span className="hero-title block text-[clamp(3.75rem,11vw,7.5rem)] leading-[0.94]">
                {dict.hero.headline}
              </span>

              {/* 主標下方的彩色斜筆觸 */}
              <span
                aria-hidden="true"
                className="gradient-accent mx-auto mt-1 block h-2 w-24 -skew-x-[28deg] rounded-sm sm:w-32 lg:mx-0"
              />

              <span className="mt-5 flex items-center justify-center gap-3 sm:gap-4 lg:justify-start">
                <span
                  aria-hidden="true"
                  className="hero-rule hero-rule--flip w-8 flex-none sm:w-14"
                />
                <span className="font-display pl-[0.35em] text-2xl font-bold tracking-[0.35em] text-[var(--blue-900)] sm:text-3xl lg:text-4xl">
                  {dict.hero.headlineAccent}
                </span>
                <span
                  aria-hidden="true"
                  className="hero-rule w-8 flex-none sm:w-14"
                />
              </span>
            </h1>

            {/* 輪播副標 */}
            <p className="mt-7 min-h-[2.25rem] text-lg font-semibold text-[var(--blue-800)]/85 sm:text-xl lg:text-2xl">
              <TypeAnimation
                key={locale}
                sequence={dict.hero.subHeadlines}
                speed={45}
                deletionSpeed={55}
                repeat={Infinity}
                wrapper="span"
              />
            </p>

            {/* CTA */}
            <div className="mt-9 flex justify-center lg:justify-start">
              <Link
                href="https://admin.quickdash.com.tw/"
                target="_blank"
                className="hero-cta group inline-flex items-center gap-3 rounded-full px-8 py-3.5 text-lg font-bold text-[#6b4318] transition-transform duration-300 hover:scale-[1.04] sm:px-10 sm:py-4 sm:text-xl"
              >
                {dict.hero.ctaSignUp}
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/50">
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            </div>

            {/* 平台 icon */}
            <div className="mt-11 flex flex-wrap items-center justify-center gap-3 sm:gap-4 lg:justify-start">
              <span className="sr-only">{dict.hero.platformsLabel}</span>
              {PLATFORMS.map((p) => (
                <div
                  key={p.name}
                  title={p.name}
                  className="hero-chip flex h-12 w-12 items-center justify-center rounded-2xl sm:h-14 sm:w-14"
                >
                  <Image
                    src={p.src}
                    alt={p.name}
                    width={34}
                    height={34}
                    className="h-7 w-7 object-contain sm:h-8 sm:w-8"
                  />
                </div>
              ))}

              <div
                title="more+"
                className="hero-chip flex h-12 w-12 items-center justify-center rounded-2xl text-[11px] font-bold tracking-tight text-[var(--blue-500)] sm:h-14 sm:w-14 sm:text-xs"
              >
                {dict.hero.chip}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 44 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative mx-auto w-full min-w-0 max-w-[560px] lg:max-w-none"
          >
            <div className="hero-tilt relative">
              <div className="hero-frame rounded-xl p-2 sm:p-2.5">
                <div className="flex items-center gap-1 px-1 pb-2 pt-0.5">
                  {TABS.map((t) => {
                    const on = t.key === ACTIVE_TAB;
                    return (
                      <span
                        key={t.key}
                        className={`hero-tab ${on ? "hero-tab--on" : ""}`}
                      >
                        {t.src ? (
                          <Image
                            src={t.src}
                            alt=""
                            width={16}
                            height={16}
                            className={`h-4 w-4 flex-none object-contain ${
                              on ? "" : "opacity-70"
                            }`}
                          />
                        ) : (
                          <LayoutGrid className="h-3.5 w-3.5 flex-none" />
                        )}
                        {/* 手機只留「全部」的文字，其餘縮成純 icon 才塞得下 */}
                        <span className={on ? "" : "hidden sm:inline"}>
                          {t.label ?? dict.hero.tabAll}
                        </span>
                      </span>
                    );
                  })}
                </div>

                <div className="relative rounded-md bg-white p-3 shadow-sm sm:p-4 lg:p-5">
                  <div className="flex items-center justify-between gap-3 pb-3">
                    <div className="flex items-center gap-2">
                      <Image
                        src="/icon/logo.png"
                        alt=""
                        width={24}
                        height={24}
                        className="h-6 w-6 rounded-md object-contain"
                      />
                      <span className="text-sm font-semibold text-gray-800">
                        {dict.hero.dashOverview}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-0.5 text-[11px] text-gray-500">
                        {dict.hero.dashRange}
                        <ChevronDown className="h-3 w-3" />
                      </span>
                    </div>
                    <span className="hidden items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-[11px] text-gray-500 sm:inline-flex">
                      <Calendar className="h-3 w-3" />
                      2026/08/01 - 2026/08/31
                    </span>
                  </div>

                  {/* KPI 卡 */}
                  <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
                    {kpis.map(
                      ({ key, label, value, delta, Icon, tint, stroke }) => (
                        <div
                          key={key}
                          className="rounded-xl border border-gray-100 bg-white p-2.5 shadow-[0_2px_10px_-4px_rgba(23,55,110,0.18)]"
                        >
                          <div className="flex items-center gap-1.5">
                            <span
                              className={`flex h-6 w-6 flex-none items-center justify-center rounded-lg ${tint}`}
                            >
                              <Icon className="h-3.5 w-3.5" />
                            </span>
                            <span className="truncate text-[11px] text-gray-500">
                              {label}
                            </span>
                          </div>
                          <div className="mt-1.5 text-base font-bold tracking-tight text-gray-900 sm:text-lg">
                            {value}
                          </div>
                          <div className="mt-1 flex items-center gap-2">
                            <span className="flex-none text-[10px] font-semibold text-emerald-500">
                              ▲ {delta}
                            </span>
                            <span className="min-w-0 flex-1">
                              <Sparkline data={SPARKS[key]} stroke={stroke} />
                            </span>
                          </div>
                        </div>
                      ),
                    )}
                  </div>

                  {/* 趨勢 + 佔比 */}
                  <div className="mt-2.5 grid gap-2.5 lg:grid-cols-[1.65fr_1fr]">
                    {/* 折線圖 */}
                    <div className="rounded-xl border border-gray-100 bg-white p-3 shadow-[0_2px_10px_-4px_rgba(23,55,110,0.18)]">
                      <div className="flex items-center gap-4">
                        <span className="text-xs font-semibold text-gray-800">
                          {dict.hero.trendTitle}
                        </span>
                        <span className="flex items-center gap-1 text-[10px] text-gray-500">
                          <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                          {dict.hero.seriesImpressions}
                        </span>
                        <span className="flex items-center gap-1 text-[10px] text-gray-500">
                          <span className="h-1.5 w-1.5 rounded-full bg-orange-400" />
                          {dict.hero.seriesClicks}
                        </span>
                      </div>

                      <div className="mt-2 flex gap-1.5">
                        <div className="flex w-7 flex-none flex-col justify-between text-[9px] leading-none text-gray-400">
                          {Y_LABELS.map((l) => (
                            <span key={l}>{l}</span>
                          ))}
                        </div>

                        <div className="relative min-w-0 flex-1">
                          <svg
                            viewBox={`0 0 ${CHART_W} ${CHART_H}`}
                            className="h-auto w-full"
                            aria-hidden="true"
                          >
                            {[0, 0.25, 0.5, 0.75, 1].map((t) => (
                              <line
                                key={t}
                                x1="0"
                                y1={t * CHART_H}
                                x2={CHART_W}
                                y2={t * CHART_H}
                                stroke="#eef2f7"
                                strokeWidth="1"
                              />
                            ))}
                            <line
                              x1={xAt(HL)}
                              y1="0"
                              x2={xAt(HL)}
                              y2={CHART_H}
                              stroke="#cbd5e1"
                              strokeWidth="1"
                              strokeDasharray="4 4"
                            />
                            <polyline
                              points={CLICK_POINTS}
                              fill="none"
                              stroke="#fb923c"
                              strokeWidth="2.4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <polyline
                              points={IMPR_POINTS}
                              fill="none"
                              stroke="#3b82f6"
                              strokeWidth="2.4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            {trendClick.map((v, i) => (
                              <circle
                                key={`c${i}`}
                                cx={xAt(i)}
                                cy={yAt(v)}
                                r="2.6"
                                fill="#fb923c"
                              />
                            ))}
                            {trendImpr.map((v, i) => (
                              <circle
                                key={`i${i}`}
                                cx={xAt(i)}
                                cy={yAt(v)}
                                r="2.6"
                                fill="#3b82f6"
                              />
                            ))}
                            <circle
                              cx={xAt(HL)}
                              cy={yAt(trendImpr[HL])}
                              r="5"
                              fill="#fff"
                              stroke="#3b82f6"
                              strokeWidth="3"
                            />
                          </svg>

                          {/* tooltip */}
                          <div
                            className="pointer-events-none absolute top-1 hidden -translate-x-1/2 sm:block"
                            style={{ left: `${(HL / (TREND_LEN - 1)) * 100}%` }}
                          >
                            <div className="rounded-lg bg-gray-900/90 px-3 py-2 text-[10px] leading-tight text-white shadow-lg">
                              <div className="mb-1 text-gray-300">08/21</div>
                              <div className="flex items-center justify-between gap-3 whitespace-nowrap">
                                <span className="text-gray-300">
                                  {dict.hero.seriesImpressions}
                                </span>
                                <span className="font-semibold tabular-nums">
                                  1,032,456
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-3 whitespace-nowrap">
                                <span className="text-gray-300">
                                  {dict.hero.seriesClicks}
                                </span>
                                <span className="font-semibold tabular-nums">
                                  28,543
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="mt-1 flex justify-between text-[9px] text-gray-400">
                            {X_LABELS.map((l) => (
                              <span key={l}>{l}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 甜甜圈 */}
                    <div className="rounded-xl border border-gray-100 bg-white p-3 shadow-[0_2px_10px_-4px_rgba(23,55,110,0.18)]">
                      <div className="text-xs font-semibold text-gray-800">
                        {dict.hero.pieTitle}
                      </div>
                      <div className="mt-2 flex items-center gap-3">
                        <div
                          className="relative h-[68px] w-[68px] flex-none rounded-full"
                          style={{ background: DONUT_GRADIENT }}
                        >
                          <div className="absolute inset-[26%] rounded-full bg-white" />
                        </div>
                        <ul className="min-w-0 flex-1 space-y-[3px]">
                          {SHARE.map((s) => (
                            <li
                              key={s.name ?? "other"}
                              className="flex items-center gap-1.5 text-[9.5px] leading-none"
                            >
                              <span
                                className="h-1.5 w-1.5 flex-none rounded-full"
                                style={{ background: s.color }}
                              />
                              <span className="truncate text-gray-600">
                                {s.name ?? dict.hero.otherLabel}
                              </span>
                              <span className="ml-auto flex-none font-semibold tabular-nums text-gray-800">
                                {s.pct}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* 媒體成效表 */}
                  <div className="mt-2.5 hidden rounded-xl border border-gray-100 bg-white p-3 shadow-[0_2px_10px_-4px_rgba(23,55,110,0.18)] sm:block">
                    <div className="mb-1.5 text-xs font-semibold text-gray-800">
                      {dict.hero.tableTitle}
                    </div>
                    <table className="w-full">
                      <thead>
                        <tr className="text-[9.5px] text-gray-400">
                          {dict.hero.tableCols.map((c, i) => (
                            <th
                              key={c}
                              className={`pb-1 font-medium ${
                                i === 0 ? "text-left" : "text-right"
                              }`}
                            >
                              {c}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {TABLE_ROWS.map((r) => (
                          <tr
                            key={r.name ?? "other"}
                            className="border-t border-gray-50 text-[10px] text-gray-700"
                          >
                            <td className="py-[5px]">
                              <span className="flex items-center gap-1.5">
                                {r.icon ? (
                                  <Image
                                    src={r.icon}
                                    alt=""
                                    width={14}
                                    height={14}
                                    className="h-3.5 w-3.5 flex-none object-contain"
                                  />
                                ) : (
                                  <span className="h-3.5 w-3.5 flex-none rounded-full bg-gray-200" />
                                )}
                                <span className="truncate">
                                  {r.name ?? dict.hero.otherLabel}
                                </span>
                              </span>
                            </td>
                            <td className="py-[5px] text-right tabular-nums">
                              <span className="flex items-center justify-end gap-1.5">
                                {r.impr}
                                <span
                                  className="hidden h-1.5 rounded-full bg-blue-500/75 md:block"
                                  style={{ width: r.imprBar }}
                                />
                              </span>
                            </td>
                            <td className="py-[5px] text-right tabular-nums">
                              <span className="flex items-center justify-end gap-1.5">
                                {r.click}
                                <span
                                  className="hidden h-1.5 rounded-full bg-blue-500/75 md:block"
                                  style={{ width: r.clickBar }}
                                />
                              </span>
                            </td>
                            <td className="py-[5px] text-right tabular-nums">
                              {r.conv}
                            </td>
                            <td className="py-[5px] text-right tabular-nums">
                              {r.cvr}
                            </td>
                            <td className="py-[5px] text-right font-semibold tabular-nums text-gray-900">
                              {r.value}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
